import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { useState, useEffect } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation
} from 'react-router-dom';

import ForgotPassword from './pages/ForgotPassword';
import Layout from './pages/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';

TimeAgo.addDefaultLocale(en);

/**
 * Component that ensures a user is authenticated before rendering the provided child components.
 * If a valid user session exists, the children are rendered; otherwise, the user is redirected to the login page.
 * First check sessionStorage for user.
 * If no user in session but authenticated flag exists, fetch user from API.
 * Send cookies.
 * Token invalid/expired, clear auth flag.
 * @param {Object} props - Props passed to the component.
 * @param {React.ReactNode} props.children - Child components to be rendered if the user is authenticated.
 * @return {React.ReactNode} The authenticated children or a redirection to the login page.
 */
function RequireAuth({ children }) {
  let location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const sessionUser = sessionStorage.getItem('user');

      if (sessionUser) {
        setUser(JSON.parse(sessionUser));
        setLoading(false);
        return;
      }

      const isAuthenticated = localStorage.getItem('isAuthenticated');

      if (isAuthenticated === 'true') {
        try {
          const response = await fetch(
            process.env.REACT_APP_PORTAL_API + '/api/v1/auth/me',
            {
              credentials: 'include'
            }
          );

          if (response.ok) {
            const data = await response.json();
            sessionStorage.setItem('user', JSON.stringify(data.user));
            setUser(data.user);
          } else {
            localStorage.removeItem('isAuthenticated');
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          localStorage.removeItem('isAuthenticated');
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  if (loading) {
    return <div></div>;
  }

  if (!user) {
    // Redirect user to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

const App = () => {
  const { status } = useThemeSwitcher();

  // Avoid theme change flicker
  if (status !== 'loaded') {
    return <div></div>;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/reset-password/:email/:token"
          element={<ResetPassword />}
        />
        <Route
          path="*"
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
